import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useGetMeQuery } from '../../store/api/authApi';
import { clearAuthData, isAuthenticated } from '../../utils/tokenManager';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';

// Simple SVG Icons
const MenuIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    className="w-6 h-6"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const UserIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg
    className="w-5 h-5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

interface HeaderProps {
  onMenuToggle?: () => void;
  isMenuOpen?: boolean;
}

export function Header({ onMenuToggle, isMenuOpen }: HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const auth = isAuthenticated();
  const { data: userData } = useGetMeQuery(undefined, { skip: !auth });

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 10);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    clearAuthData();
    setIsUserMenuOpen(false);
    navigate('/login');
  };

  const user = userData?.data?.user;
  const userTelegramId = user?.telegramId || user?._id;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${
        isScrolled
          ? 'bg-brand-primary/30 backdrop-blur-lg backdrop-saturate-150 border-brand-primary/40 shadow-lg'
          : 'bg-transparent backdrop-blur-0 backdrop-saturate-100 border-transparent shadow-none'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo and Menu Toggle - Automatically positioned by dir attribute */}
          <div className="flex items-center gap-4">
            {auth && onMenuToggle && (
              <button
                type="button"
                onClick={onMenuToggle}
                className="p-2 text-neutral-light hover:text-brand-accent transition-colors duration-200 rounded-lg hover:bg-neutral-dark/50"
                aria-label={isMenuOpen ? t('navigation.closeMenu') : t('navigation.menu')}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            )}
            <Link
              to={auth ? '/dashboard' : '/'}
              className="flex items-center gap-3 group transition-transform duration-300 hover:scale-105"
              aria-label="NOVA UP - SLIMAN SA Home"
            >
              <span className="hidden sm:block text-xl md:text-2xl font-heading font-bold text-brand-accent group-hover:text-brand-accent/80 transition-colors duration-200">
                NOVA UP - SLIMAN SA
              </span>
            </Link>
          </div>

          {/* User Actions - Automatically positioned by dir attribute */}
          <div className="flex items-center gap-3 md:gap-4">
            {/* Language Switcher */}
            <div className="hidden sm:block">
              <LanguageSwitcher />
            </div>

            {/* User Menu */}
            {auth && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-dark/50 hover:bg-neutral-dark transition-colors duration-200 text-neutral-light group"
                  aria-label="User menu"
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <UserIcon />
                  <span className="hidden md:block font-body text-sm">
                    {user?.firstName || user?.email || 'User'}
                  </span>
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && userTelegramId && (
                  <div className="absolute top-full mt-2 w-48 bg-neutral-dark/95 backdrop-blur-lg rounded-lg shadow-xl border border-neutral-dark/50 overflow-hidden animate-slide-down end-0">
                    <Link
                      to={`/admin/users/${userTelegramId}`}
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 text-neutral-light hover:bg-brand-accent/20 hover:text-brand-accent transition-colors duration-200"
                    >
                      <UserIcon />
                      <span className="font-body">{t('navigation.profile')}</span>
                    </Link>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-neutral-light hover:bg-danger/20 hover:text-danger transition-colors duration-200 text-start"
                    >
                      <LogoutIcon />
                      <span className="font-body">{t('navigation.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close user menu */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
}

